import { Component, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, finalize, iif, Observable, of, switchMap, take } from 'rxjs';
import { API, ContentShareFullDTO, DistributionBasis, Pagination, PlanningDTO } from 'src/app/services/api/api';
import { ModalService } from 'src/app/services/gui/modal/modal.service';
import { SbSplitButton, SelectionState } from 'src/app/services/global/global-interfaces';
import { PermissionService } from 'src/app/services/permission.service';
import { TagService } from 'src/app/services/tag.service';
import { UtilService } from 'src/app/services/util.service';
import { ErrorService } from '../../../services/error.service';
import { QueryParamService } from '../../../services/lists/query-params';
import { DiscussionService } from '../../../services/discussion.service';
import { ContentShareFullDTOAndTags } from '../../../models/taggable.model';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { NavigationService } from '@ngx-seatback/utils';
import { SbPlanningContentShareComponent } from '../../../components/sb-planning-content-share/sb-planning-content-share.component';
import { SbPlanningTimeComponent } from '../../../components/booking-editor/sb-planning-time/sb-planning-time.component';
import { SbLocalDateTime } from '../../../services/api/api-date-time';
import { DateTime } from 'luxon';
import { HierarchyService } from '../../../services/transformation-helper/hierarchy.service';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { isNumberEven } from '../../../services/global/global-methods';
import { MultiSelectButtonAction } from '../../../components/table-multi-select-button/table-multi-select-button.component';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelComponent, FilterPanelConfig } from '@ngx-seatback/layout';

type SplitButtonAction = 'delete' | 'create' | 'edit' | 'copy';
type ContentShareFullDTOAndTagsAndButtons = ContentShareFullDTO & SbSplitButton<SplitButtonAction>;

@Component({
  selector: 'sb-page-content-share',
  templateUrl: './content-share.component.html',
  styleUrls: ['./content-share.component.scss'],
  standalone: false,
})
export class ContentShareComponent implements OnInit {
  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;

  private readonly hierarchy = inject(HierarchyService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);
  showChildren: Map<number, ContentShareFullDTOAndTagsAndButtons>;
  contentShareDragged: number;
  isDragging: boolean;
  loading = signal(true);

  contentShares!: ContentShareFullDTOAndTagsAndButtons[];
  pagination: BehaviorSubject<Pagination>;

  showSideBar: boolean;

  selectedContentShare: ContentShareFullDTOAndTagsAndButtons | null;
  private queryParamsParsed: boolean;

  submissions: Map<number, ContentShareFullDTOAndTagsAndButtons> = new Map<number, ContentShareFullDTOAndTagsAndButtons>();
  planning: PlanningDTO;
  plannings: PlanningDTO[];
  submissionID: number;

  from: SbLocalDateTime | null = new SbLocalDateTime(DateTime.local().startOf('day'));
  till: SbLocalDateTime | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> = null;
  // refactor into computed of selectedItems when transforming selectedItems to signal and remove recalculateSelectionState
  selectionState: SelectionState = 'none';

  protected readonly isNumberEven = isNumberEven;

  // filter options
  search: string = '';
  exactMatch: SearchOptions = 'contains';
  protected filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: { visibleWithoutChanges: true },
  };

  constructor(
    protected readonly api: API,
    private readonly errorService: ErrorService,
    public readonly util: UtilService,
    public readonly permissionService: PermissionService,
    public readonly tagService: TagService,
    public readonly discussionService: DiscussionService,
    private readonly modal: ModalService,
    protected readonly translate: TranslateService,
    private readonly queryParamService: QueryParamService,
    private readonly navService: NavigationService
  ) {
    this.submissionID = null;
    this.contentShares = [];
    this.showChildren = new Map();
    this.contentShareDragged = undefined;
    this.isDragging = false;
    this.selectedContentShare = null;
    this.queryParamsParsed = false;
  }

  ngOnInit(): void {
    if (!this.queryParamsParsed) {
      this.parseQueryParams().then(() => {
        this.queryParamsParsed = true;
        this.initialContentShareRetrieve();
      });
    }
  }

  private initialContentShareRetrieve() {
    if (this.planning) {
      // with planning
      this.api
        .ds_cm_ContentShare_retrieveHierarchyInPlanning(this.planning.id, {
          planning_submission_id: this.submissionID,
          from: this.from,
          till: this.till,
        })
        .subscribe({
          next: (shares: ContentShareFullDTOAndTags[]) => {
            this.contentShares = shares;
            if (this.permissionService.hasPermission(this.permissionService.Permission.tag_list)) {
              this.contentShares = this.tagService.getAndAddTagEntities(this.tagService.TaggableDTO.ContentShare, this.contentShares);
            }
          },
          error: error => {
            this.errorService.errorHandler(error);
          },
        });
    } else {
      // without planning / normal
      this.api.ds_cm_ContentShare_retrieveHierarchy({ from: this.from, till: this.till }).subscribe({
        next: shares => {
          this.contentShares = shares;
          if (this.permissionService.hasPermission(this.permissionService.Permission.tag_list)) {
            this.contentShares = this.tagService.getAndAddTagEntities(this.tagService.TaggableDTO.ContentShare, this.contentShares);
          }
        },
        error: error => {
          this.errorService.errorHandler(error);
        },
      });
    }
  }

  parseQueryParams(): Promise<void> {
    return new Promise(res => {
      this.parseFilterQueryParams().then(res);
    });
  }

  parseFilterQueryParams(): Promise<void> {
    return new Promise(res => {
      const planningQp = this.queryParamService.getQueryParam('planning');
      const submissionQP = +this.queryParamService.getQueryParam('submission');
      if (submissionQP && !isNaN(+submissionQP)) {
        this.submissionID = +submissionQP;
      }

      if (planningQp && !isNaN(+planningQp)) {
        this.api
          .ds_cm_Planning_retrieveById(+planningQp)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe(planning => {
            this.planning = planning;
            res();
          });
      } else {
        res();
      }
    });
  }

  refreshQueryParams() {
    if (this.planning) {
      this.queryParamService.setQueryParam('planning', this.planning.id.toString());
    } else {
      this.queryParamService.clearQueryParam('planning');
    }
  }

  toggleSideBar() {
    this.showSideBar = !this.showSideBar;
  }

  requestDebounced(pagination?: BehaviorSubject<Pagination>) {
    if (pagination) {
      this.pagination = pagination;
    }

    if (this.queryParamsParsed) {
      this.refreshQueryParams();
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshContentShares();
    }, 500);
  }

  refreshContentShares() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    this.loading.set(true);
    this.contentShares = [];
    const preconditionIds = Array.from(this.showChildren.keys());
    let obsHierarchy: Observable<ContentShareFullDTO[]>;
    let obsRetrieveAll: Observable<ContentShareFullDTO[]>;
    if (this.planning) {
      obsHierarchy = this.api.ds_cm_ContentShare_retrieveHierarchyInPlanning(
        this.planning.id,
        {
          planning_submission_id: this.submissionID,
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          from: this.from,
          till: this.till,
        },
        this.pagination
      );
      obsRetrieveAll = this.api.ds_cm_ContentShare_retrieveAllInPlanning(
        this.planning.id,
        {
          precondition: preconditionIds,
          planning_submission_id: this.submissionID,
          from: this.from,
          till: this.till,
        },
        new BehaviorSubject<Pagination>({ page: 1, count: 500, resultcount: -1, pages: -1 })
      );
    } else {
      obsHierarchy = this.api.ds_cm_ContentShare_retrieveHierarchy(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          from: this.from,
          till: this.till,
        },
        this.pagination
      );
      obsRetrieveAll = this.api.ds_cm_ContentShare_retrieveAll(
        { precondition: preconditionIds, from: this.from, till: this.till },
        new BehaviorSubject<Pagination>({ page: 1, count: 500, resultcount: -1, pages: -1 })
      );
    }
    obsHierarchy
      .pipe(
        switchMap(shares => this.tagService.getAndAddTagEntitiesObs(this.tagService.TaggableDTO.ContentShare, shares)),
        switchMap(contentShares => {
          this.contentShares = this.buildSplitButtons(contentShares);

          return iif(() => preconditionIds.length === 0, of<ContentShareFullDTO[]>([]), obsRetrieveAll);
        }),
        switchMap(contentShares => this.tagService.getAndAddTagEntitiesObs(this.tagService.TaggableDTO.ContentShare, contentShares)),

        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
          this.isDragging = false;
        })
      )
      .subscribe(contentSharesRetrieveAll => {
        contentSharesRetrieveAll = this.buildSplitButtons(contentSharesRetrieveAll);
        const map = preconditionIds.map(preconditionId => {
          return { id: preconditionId, preconditions: [] };
        });
        contentSharesRetrieveAll.forEach(contentShare => {
          const toAdd = map.find(tempObj => tempObj.id === contentShare.precondition?.id);
          if (toAdd?.preconditions) {
            toAdd.preconditions.push(contentShare);
          }
        });
        for (const tempObj of map) {
          const searchContentShare = this.hierarchy.getObjectFromHierarchyArrayById(this.contentShares, tempObj, 'preconditionOf');

          if (searchContentShare) {
            searchContentShare.preconditionOf = tempObj.preconditions;
          }
        }
      });
  }

  private updateContentShareHierarchy(currentContentShare: ContentShareFullDTO): void {
    this.loading.set(true);
    let obsHierarchy: Observable<ContentShareFullDTO[]>;
    if (this.planning) {
      obsHierarchy = this.api.ds_cm_ContentShare_retrieveHierarchyInPlanning(
        this.planning.id,
        {
          planning_submission_id: this.submissionID,
          precondition: currentContentShare.id,
          from: this.from,
          till: this.till,
        },
        new BehaviorSubject<Pagination>({ page: 1, count: 500, resultcount: -1, pages: -1 })
      );
    } else {
      obsHierarchy = this.api.ds_cm_ContentShare_retrieveHierarchy(
        {
          precondition: currentContentShare.id,
          from: this.from,
          till: this.till,
        },
        new BehaviorSubject<Pagination>({ page: 1, count: 500, resultcount: -1, pages: -1 })
      );
    }
    obsHierarchy
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
          this.isDragging = false;
        })
      )
      .subscribe({
        next: (e: ContentShareFullDTO[]) => {
          currentContentShare.preconditionOf = this.buildSplitButtons(e);
        },
        error: error => {
          this.errorService.errorHandler(error);
        },
      });
  }

  editContentShare(contentShare: ContentShareFullDTO) {
    this.selectedContentShare = { ...contentShare };
    this.showSideBar = true;
  }

  deleteContentShare(contentShare: ContentShareFullDTO) {
    const title: string = this.translate.instant('lang.globals.deleteContentShare');
    const message: string = this.translate.instant('lang.globals.deleteContentShareMessage', { value: contentShare.name });
    this.modal
      .confirm({
        title,
        message,
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then(res => {
        if (res) {
          this.loading.set(true);
          let obsDelete: Observable<boolean>;
          if (this.planning) {
            obsDelete = this.api.ds_cm_ContentShare_deleteByIdInPlanning(contentShare.id, this.planning.id);
          } else {
            obsDelete = this.api.ds_cm_ContentShare_deleteById(contentShare.id);
          }
          obsDelete.subscribe({
            next: data => {
              if (data) {
                this.requestDebounced();
              }
            },
          });
        }
      });
  }

  updateContentShares($event: ContentShareFullDTO) {
    this.showSideBar = false;
    this.selectedContentShare = null;
    if ($event) {
      this.requestDebounced();
    }
  }

  toggleShowChildren(share: ContentShareFullDTO) {
    if (this.showChildren.has(share.id)) {
      this.removeVisibility(share);
    } else {
      this.showChildren.set(share.id, share);
      this.updateContentShareHierarchy(share);
    }
  }

  private removeVisibility(share: ContentShareFullDTO) {
    this.showChildren.delete(share.id);
    const traversedChildren = this.traverseChildren(share, []);
    traversedChildren?.forEach(child => this.showChildren.delete(child.id));
  }

  traverseChildren(contentShare: ContentShareFullDTO, result: ContentShareFullDTO[]): ContentShareFullDTO[] {
    if (!contentShare.preconditionOf || !Array.isArray(contentShare.preconditionOf)) {
      return result;
    }
    result.push(...contentShare.preconditionOf);
    for (const child of contentShare.preconditionOf) {
      this.traverseChildren(child, result);
    }
    return result;
  }

  onDragStart($event: any) {
    $event.dataTransfer.setData('shareid', $event.target.attributes['data-share'].value);
    $event.dataTransfer.dropEffect = 'move';
    this.contentShareDragged = +$event.target.attributes['data-share'].value;
  }

  onDragEnd($event: any) {
    this.isDragging = false;
  }

  onDragOver($event: any) {
    let target = $event.currentTarget;
    while (target.nodeName !== 'TR') {
      target = target.parentNode;
    }
    const draggedContentShare: number = +this.contentShareDragged;
    const targetContentShare: number = target.attributes['data-share'] ? +target.attributes['data-share'].value : null;

    if (draggedContentShare === 0) {
      return;
    }

    if (draggedContentShare === targetContentShare) {
      return;
    }

    const draggedContentShareObj: ContentShareFullDTO = this.hierarchy.getObjectFromHierarchyArrayById(
      this.contentShares,
      {
        id: draggedContentShare,
      },
      'preconditionOf'
    );
    const targetContentShareObj: ContentShareFullDTO = this.hierarchy.getObjectFromHierarchyArrayById(
      this.contentShares,
      {
        id: targetContentShare,
      },
      'preconditionOf'
    );

    if (!this.allowSubDrop(draggedContentShareObj, targetContentShareObj)) {
      return;
    }

    $event.preventDefault();
  }

  allowSubDrop(draggedContentShareObj: ContentShareFullDTO, targetContentShareObj: ContentShareFullDTO): boolean {
    if (!targetContentShareObj) {
      return true;
    }

    let currentContentShare: ContentShareFullDTO = targetContentShareObj;

    while (currentContentShare) {
      if (currentContentShare.id === draggedContentShareObj.id) {
        return false;
      }
      currentContentShare = this.hierarchy.getObjectFromHierarchyArrayById(
        this.contentShares,
        currentContentShare.precondition,
        'preconditionOf'
      );
    }
    return true;
  }

  onDrop($event: any, index?: number) {
    let target = $event.currentTarget;
    while (target.nodeName !== 'TR') {
      target = target.parentNode;
    }
    const draggedContentShare: number = +this.contentShareDragged;
    const targetContentShare: number = target.attributes['data-share'] ? +target.attributes['data-share'].value : null;

    const draggedContentShareObj: ContentShareFullDTO = this.hierarchy.getObjectFromHierarchyArrayById(
      this.contentShares,
      {
        id: draggedContentShare,
      },
      'preconditionOf'
    );
    const targetContentShareObj: ContentShareFullDTO = this.hierarchy.getObjectFromHierarchyArrayById(
      this.contentShares,
      {
        id: targetContentShare,
      },
      'preconditionOf'
    );

    this.hierarchy.removeObjectFromHierarchyArrayById(this.contentShares, draggedContentShareObj, 'preconditionOf');

    if (targetContentShareObj) {
      if (index != null) {
        draggedContentShareObj.priority = index;
        targetContentShareObj.preconditionOf.splice(index, 0, draggedContentShareObj);
      } else {
        targetContentShareObj.preconditionOf.push(draggedContentShareObj);
      }
      if (!this.showChildren.has(targetContentShareObj.id)) {
        this.removeVisibility(targetContentShareObj);
      }
    } else {
      if (index != null) {
        draggedContentShareObj.priority = this.contentShares[index]
          ? this.contentShares[index].priority
          : this.contentShares[this.contentShares.length - 1].priority + 1;
      }
      this.contentShares.push(draggedContentShareObj);
    }

    this.refreshParents();

    this.refreshContentShareDragged(draggedContentShareObj);
  }

  refreshContentShareDragged(draggedContentShareObj: ContentShareFullDTO) {
    const tempContentShare: ContentShareFullDTO = { ...draggedContentShareObj };
    tempContentShare.preconditionOf = [];

    if (tempContentShare.precondition) {
      tempContentShare.precondition.preconditionOf = [];
      tempContentShare.precondition.precondition = null;
    }

    let obsEdit: Observable<ContentShareFullDTO>;
    if (this.planning) {
      if (draggedContentShareObj.planning) {
        obsEdit = this.api.ds_cm_ContentShare_editContentShareInPlanning(this.planning.id, tempContentShare);
      } else {
        obsEdit = this.api.ds_cm_ContentShare_createContentShareOverwriteInPlanning(
          this.planning.id,
          draggedContentShareObj.id,
          tempContentShare
        );
      }
    } else {
      obsEdit = this.api.ds_cm_ContentShare_editContentShare(tempContentShare);
    }
    // no pipe-ing on edit
    obsEdit.subscribe(() => {
      this.requestDebounced();
    });
  }

  refreshParents() {
    for (const contentShare of this.contentShares) {
      this.refreshParent(contentShare, null);
    }
  }

  refreshParent(contentShare: ContentShareFullDTO, parent?: ContentShareFullDTO) {
    contentShare.precondition = parent;
    if (contentShare.preconditionOf && Array.isArray(contentShare.preconditionOf)) {
      for (const child of contentShare.preconditionOf) {
        this.refreshParent(child, contentShare);
      }
    }
  }

  onmousedown($event: any) {
    let target = $event.target;
    while (target.nodeName !== 'TR') {
      target = target.parentNode;
    }
    target.setAttribute('draggable', 'true');
  }

  onmouseup($event: any) {
    let target = $event.target;
    while (target.nodeName !== 'TR') {
      target = target.parentNode;
    }
    target.setAttribute('draggable', 'false');
  }

  createNewContentShare(precondition?: ContentShareFullDTO) {
    this.selectedContentShare = new ContentShareFullDTO();
    this.selectedContentShare.quotas = [];
    this.selectedContentShare.quotaDistributionBasis = DistributionBasis.Count;
    if (precondition) {
      this.selectedContentShare.precondition = precondition;
      this.selectedContentShare.priority = precondition.preconditionOf?.length ?? 0;
      this.selectedContentShare.quotaDistributionBasis = precondition.quotaDistributionBasis;
      this.selectedContentShare.validFrom = precondition.validFrom;
      this.selectedContentShare.validTill = precondition.validTill;
      this.selectedContentShare.quotas = precondition.quotas.map(quota => {
        return { ...quota, id: null };
      });
    }
  }

  copyContentShare(share: ContentShareFullDTO) {
    const copy = new ContentShareFullDTO();
    copy.name = share.name;
    copy.description = share.description;
    copy.playerCull = share.playerCull;
    copy.timeCull = share.timeCull;
    copy.geoCull = share.geoCull;
    copy.stateSpaceCull = share.stateSpaceCull;
    copy.diversify = share.diversify;
    copy.precondition = share.precondition;
    copy.preconditionOf = [];
    copy.priority = share.priority + 1;
    copy.validFrom = share.validFrom;
    copy.validTill = share.validTill;
    copy.quotaDistributionBasis = share.quotaDistributionBasis;
    copy.id = null;
    copy.quotas = share.quotas.map(quota => {
      return { ...quota, id: null };
    });
    this.selectedContentShare = copy;
    this.showSideBar = true;
  }

  selectForSubmission(item: ContentShareFullDTO) {
    const found = this.submissions.has(item.id);
    if (found) {
      this.submissions.delete(item.id);
    } else {
      this.submissions.set(item.id, item);
    }
    this.recalculateSelectionState();
  }

  private recalculateSelectionState() {
    if (!this.contentShares || this.contentShares.length === 0) {
      this.selectionState = 'none';
      return;
    }
    const allSelectedOnCurrentPage = this.contentShares.every(campaign => this.submissions.has(campaign.id));
    if (allSelectedOnCurrentPage) {
      this.selectionState = 'all';
      return;
    }
    const someSelectedOnCurrentPage = this.contentShares.some(campaign => this.submissions.has(campaign.id));
    if (someSelectedOnCurrentPage) {
      this.selectionState = 'partly';
      return;
    }
    this.selectionState = 'none';
  }

  submitPlanningModal() {
    this.modal
      .confirm({
        title: this.translate.instant('lang.bookingEditor.modal.submitPlanningHeadline', { planningName: this.planning.name }),
        message: this.translate.instant('lang.bookingEditor.modal.submitPlanning', { planningName: this.planning.name }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .ds_cm_Planning_submitById(this.planning.id, {
              name: this.planning.name,
              description: this.translate.instant('lang.planning.completePlanning'),
              forCustomer: this.planning.forCustomer ? this.planning.forCustomer.id : null,
              deleteOtherPlannings: false,
            })
            .subscribe(() => {
              this.navService.navigateToPoint(RoutingID.plannings).then();
            });
        }
      })
      .catch(() => {});
  }

  submitPartialPlanningModal() {
    this.modal
      .showNavigationModal({
        entryComponent: { value: SbPlanningContentShareComponent, data: { submissions: this.submissions } },
        title: this.translate.instant('lang.globals.submitPartialPlanning'),
        icon: 'planningSubmit',
        modalOptions: { windowClass: 'bigXL' },
      })
      .then((res: { name: string; description: string }) => {
        if (res) {
          this.api
            .ds_cm_Planning_submitById(this.planning.id, {
              name: res.name,
              description: res.description ? res.description : null,
              forCustomer: this.planning.forCustomer ? this.planning.forCustomer.id : null,
              contentShares: Array.from(this.submissions.keys()),
              deleteOtherPlannings: false,
            })
            .subscribe(() => {
              this.navService.navigateToPoint(RoutingID.plannings).then();
            });
        }
      })
      .catch(() => {});
  }

  submitPlanningDateModal() {
    this.modal
      .showNavigationModal({
        entryComponent: { value: SbPlanningTimeComponent, data: null },
        title: this.translate.instant('lang.globals.submitPlanningDate'),
        icon: 'planningSubmit',
        modalOptions: { size: 'lg' },
      })
      .then((res: { name: string; description: string; from: SbLocalDateTime; till: SbLocalDateTime }) => {
        if (res) {
          this.api
            .ds_cm_Planning_submitById(this.planning.id, {
              name: res.name,
              description: res.description ? res.description : null,
              forCustomer: this.planning.forCustomer ? this.planning.forCustomer.id : null,
              from: res.from,
              till: res.till,
              deleteOtherPlannings: false,
            })
            .subscribe(() => {
              this.navService.navigateToPoint(RoutingID.plannings);
            });
        }
      })
      .catch(() => {});
  }

  acceptPlanning(planning: PlanningDTO) {
    this.modal
      .confirm({
        title: this.translate.instant('lang.bookingEditor.modal.acceptPlanningHeadline', { planningName: planning.name }),
        message: this.translate.instant('lang.bookingEditor.modal.acceptPlanningWithoutDelete', { planningName: planning.name }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .ds_cm_Planning_acceptById(planning.id, this.submissionID)
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: () => {
                this.navService.navigateToPoint(RoutingID.partialPlanning, [{ id: 'planningID', value: planning?.id }]);
              },
              error: error => {
                this.errorService.errorHandler(error);
              },
            });
        }
      })
      .catch(() => {});
  }

  rejectPlanning(planning: PlanningDTO) {
    this.modal
      .confirm({
        title: this.translate.instant('lang.bookingEditor.modal.rejectPlanningHeadline'),
        message: this.translate.instant('lang.bookingEditor.modal.rejectPlanning'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .ds_cm_Planning_withdrawSubmissionById(planning.id, this.submissionID)
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: () => {
                this.navService.navigateToPoint(RoutingID.partialPlanning, [{ id: 'planningID', value: planning?.id }]);
              },
              error: error => {
                this.errorService.errorHandler(error);
              },
            });
        }
      });
  }

  localDateTimeInputFrom(date: SbLocalDateTime) {
    this.from = date;
    this.requestDebounced();
  }

  localDateTimeInputTill(date: SbLocalDateTime) {
    this.till = date;
    this.requestDebounced();
  }

  selectAction($event: MultiSelectButtonAction) {
    if (!this.contentShares || this.contentShares.length === 0) {
      return;
    }
    switch ($event) {
      case 'selectPage':
        this.contentShares.forEach(share => {
          if (share.planning) {
            this.submissions.set(share.id, share);
          }
        });
        Array.from(this.showChildren.values()).forEach(child => {
          if (child.planning) {
            this.submissions.set(child.id, child);
            child.preconditionOf.forEach(subchild => {
              if (subchild.planning) {
                this.submissions.set(subchild.id, subchild);
              }
            });
          }
        });

        this.selectionState = 'all';
        break;
      case 'deselectAll':
        this.submissions.clear();
        this.selectionState = 'none';
        break;
      case 'deselectPage':
      default:
        this.contentShares.forEach(share => {
          this.submissions.delete(share.id);
          const traversedChildren = this.traverseChildren(share, []);
          traversedChildren?.forEach(child => this.submissions.delete(child.id));
        });
        this.selectionState = 'none';
        break;
    }
  }

  // filter methods

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
    this.planning = filterMap.get('planning')?.[0];
  }

  checkExpansion() {
    return this.filterPanelRef?.isPanelExtended() ?? false;
  }

  openFilterPanel(elementId: HTMLDivElement) {
    if (!elementId) {
      throw new Error('Missing elementId for filter panel opening');
    }
    if (this.filterPanelRef) {
      this.filterPanelRef.isPanelExtended.set(true);
      this.scrollToFilterPanelElement(elementId, false);
    }
  }

  scrollToFilterPanelElement(element: HTMLDivElement, isInitiallyExpanded: boolean = true) {
    if (!element) {
      throw new Error('Missing elementId for filter panel opening');
    }
    const timeout = setTimeout(
      () => {
        const scrollContainer = element.closest('.custom-scrollbar') as HTMLElement;

        if (!scrollContainer) {
          console.warn('No scrollable container found for element');
          return;
        }
        const containerTop = scrollContainer.getBoundingClientRect().top;
        const elementTop = element.getBoundingClientRect().top;

        const headerOffset = 110; // height filter header + height of navbar + offset
        const offsetPosition = elementTop - containerTop + scrollContainer.scrollTop - headerOffset;

        scrollContainer.scrollTo({ behavior: 'smooth', top: offsetPosition });
        // elementId.scrollBy({ top: 40 });
        clearTimeout(timeout);
      },
      isInitiallyExpanded ? 100 : 400
    );
  }

  buildSplitButtons<T extends ContentShareFullDTO>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.content_share_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.edit',
          action: 'edit',
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.content_share_create])) {
        item.sbButtons.push({
          icon: 'plus',
          tooltip: 'lang.globals.create',
          action: 'create',
        });
        item.sbButtons.push({
          icon: 'copy',
          tooltip: 'lang.globals.copy',
          action: 'copy',
        });
      }

      if (this.permissionService.hasPermissions([this.permissionService.Permission.content_share_delete])) {
        item.sbButtons.push({
          icon: 'trash',
          iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
          action: 'delete',
          disabled: item?.preconditionOf?.length > 0 || (this.planning != null && !item.planning),
          tooltip: 'lang.globals.delete',
        });
      }
    });
    return result;
  }

  protected resolveSplitButtonAction(item: ContentShareFullDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'delete':
        this.deleteContentShare(item);
        break;
      case 'create':
        this.createNewContentShare(item);
        this.toggleSideBar();
        break;
      case 'edit':
        this.editContentShare(item);
        break;
      case 'copy':
        this.copyContentShare(item);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }
}
